import { useState } from 'react';
import { WorkItem, JobWithNotes, Appointment } from '../workItemTypes';
import { getWorkItemAssignee } from '../utils/workItemUtils';
import { computeReorder } from '../../../utils/gapIndexing';

export interface DragAndDropHandlers {
  draggedWorkItem: string | null;
  dragOverWorkItem: string | null;
  dragOverAssignee: string | null;
  handleDragStart: (e: React.DragEvent, workItemId: string) => void;
  handleDragEnd: () => void;
  handleDragOver: (e: React.DragEvent, workItemId: string) => void;
  handleDragEnter: (e: React.DragEvent, workItemId: string) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent, targetWorkItemId: string) => void;
  handleAssigneeGroupDragOver: (e: React.DragEvent, assignee: string) => void;
  handleAssigneeGroupDrop: (e: React.DragEvent, targetAssignee: string) => void;
  handleAssigneeGroupDragEnter: (e: React.DragEvent, assignee: string) => void;
  handleAssigneeGroupDragLeave: (e: React.DragEvent) => void;
}

export const useDragAndDrop = (
  workItems: WorkItem[],
  jobs: JobWithNotes[],
  appointments: Appointment[],
  onJobUpdate: (job: JobWithNotes) => void,
  onAppointmentUpdate: (appointment: Appointment) => void,
  onWorkItemReorder: (reorderedWorkItems: WorkItem[]) => void,
  onWorkItemReassign: (workItemId: string, newAssignee: string) => void
): DragAndDropHandlers => {
  const [draggedWorkItem, setDraggedWorkItem] = useState<string | null>(null);
  const [dragOverWorkItem, setDragOverWorkItem] = useState<string | null>(null);
  const [dragOverAssignee, setDragOverAssignee] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, workItemId: string) => {
    setDraggedWorkItem(workItemId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', workItemId);
  };

  const handleDragEnd = () => {
    setDraggedWorkItem(null);
    setDragOverWorkItem(null);
    setDragOverAssignee(null);
  };

  const handleDragOver = (e: React.DragEvent, workItemId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverWorkItem(workItemId);
  };

  const handleDragEnter = (e: React.DragEvent, workItemId: string) => {
    e.preventDefault();
    setDragOverWorkItem(workItemId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the entire row, not just moving between child elements
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverWorkItem(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetWorkItemId: string) => {
    e.preventDefault();
    
    if (!draggedWorkItem || draggedWorkItem === targetWorkItemId) {
      handleDragEnd();
      return;
    }

    const draggedItem = workItems.find(w => w.id === draggedWorkItem);
    const targetItem = workItems.find(w => w.id === targetWorkItemId);
    
    if (!draggedItem || !targetItem) {
      handleDragEnd();
      return;
    }

    const draggedAssignee = getWorkItemAssignee(draggedItem, jobs, appointments);
    const targetAssignee = getWorkItemAssignee(targetItem, jobs, appointments);

    // If dropping on a work item from a different assignee, reassign
    if (draggedAssignee !== targetAssignee) {
      // Update the assignee in the corresponding job or appointment
      if (draggedItem.type === 'job') {
        const job = jobs.find(j => j.id === draggedItem.id);
        if (job) {
          const updatedJob = { ...job, assignedUserId: targetAssignee };
          onJobUpdate(updatedJob);
        }
      } else if (draggedItem.type === 'appointment') {
        const appointment = appointments.find(a => a.id === draggedItem.id);
        if (appointment) {
          const updatedAppointment = { ...appointment, assignedUsers: targetAssignee };
          onAppointmentUpdate(updatedAppointment);
        }
      }
      
      onWorkItemReassign(draggedWorkItem, targetAssignee);
    } else {
      // Reorder within the same assignee (gap-based indexing)
      const groupItems = workItems
        .filter(w => getWorkItemAssignee(w, jobs, appointments) === draggedAssignee)
        .sort((a, b) => (Number(a.displayIndex) || 0) - (Number(b.displayIndex) || 0))
        .map(w => ({ id: w.id, guid: w.guid, type: w.type, displayIndex: Number(w.displayIndex) || 0 }));

      // `computeReorder` matches its source/target and keys `localUpdates` on
      // **guid**, but the drag handlers carry work item `id`s — so both ends
      // need translating. Passing ids straight through made computeReorder find
      // no source and return an empty result, and `localUpdates.get(w.id)` then
      // missed on every row, so reordering in this table silently did nothing.
      const draggedGuid = groupItems.find(i => i.id === draggedWorkItem)?.guid;
      const targetGuid = groupItems.find(i => i.id === targetWorkItemId)?.guid;

      if (draggedGuid && targetGuid) {
        const { localUpdates } = computeReorder(groupItems, draggedGuid, targetGuid);

        const updatedWorkItems = workItems.map(w => {
          const newIndex = localUpdates.get(w.guid);
          return newIndex !== undefined ? { ...w, displayIndex: newIndex } : w;
        });

        onWorkItemReorder(updatedWorkItems);
      }
    }

    handleDragEnd();
  };

  const handleAssigneeGroupDragOver = (e: React.DragEvent, assignee: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverAssignee(assignee);
  };

  const handleAssigneeGroupDrop = (e: React.DragEvent, targetAssignee: string) => {
    e.preventDefault();
    
    if (!draggedWorkItem) {
      handleDragEnd();
      return;
    }

    const draggedItem = workItems.find(w => w.id === draggedWorkItem);
    if (!draggedItem) {
      handleDragEnd();
      return;
    }

    const currentAssignee = getWorkItemAssignee(draggedItem, jobs, appointments);
    
    // Only reassign if dropping on a different assignee
    if (currentAssignee !== targetAssignee) {
      // Update the assignee in the corresponding job or appointment
      if (draggedItem.type === 'job') {
        const job = jobs.find(j => j.id === draggedItem.id);
        if (job) {
          const updatedJob = { ...job, assignedUserId: targetAssignee };
          onJobUpdate(updatedJob);
        }
      } else if (draggedItem.type === 'appointment') {
        const appointment = appointments.find(a => a.id === draggedItem.id);
        if (appointment) {
          const updatedAppointment = { ...appointment, assignedUsers: targetAssignee };
          onAppointmentUpdate(updatedAppointment);
        }
      }
      
      onWorkItemReassign(draggedWorkItem, targetAssignee);
    }

    handleDragEnd();
  };

  const handleAssigneeGroupDragEnter = (e: React.DragEvent, assignee: string) => {
    e.preventDefault();
    setDragOverAssignee(assignee);
  };

  const handleAssigneeGroupDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the entire group, not just moving between child elements
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverAssignee(null);
    }
  };

  return {
    draggedWorkItem,
    dragOverWorkItem,
    dragOverAssignee,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleAssigneeGroupDragOver,
    handleAssigneeGroupDrop,
    handleAssigneeGroupDragEnter,
    handleAssigneeGroupDragLeave,
  };
};